import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    forwardRef,
    Inject,
    Input,
    OnInit,
    Optional,
    ViewEncapsulation
} from '@angular/core';
import { buildProviders } from '@kalidea/kaligraphi';
import {
    MutationGetInitialisationAdmninistrationDocumentsArgs,
    PortailDocumentCategorieDto,
    PortailStatutDto
} from 'src/graphql/generated-types';
import gql from 'graphql-tag';
import { tap } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { CustomFormControlAccessWithLoaders } from 'src/app/_core/tools/custom-form-control-access';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import {
    DocumentBankListComponent
} from 'src/app/_shared/dialogs/document-bank-dialog/document-bank-list/document-bank-list.component';
import { wheneverEnd } from 'src/app/utils/operators.utils';
import {
    PartialDocumentBankFilters
} from 'src/app/_shared/dialogs/document-bank-dialog/document-bank-list-dialog/document-bank-list-dialog.component';

export interface DocumentBankFilters {
    categoryCode?: string;
    type?: string;
    statusId?: number;
    hasExpired?: boolean;
    isUnlinked?: boolean;
}

@Component({
    selector: 'app-document-bank-filters',
    templateUrl: './document-bank-filters.component.html',
    styleUrls: ['./document-bank-filters.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(DocumentBankFiltersComponent),
    standalone: false
})
export class DocumentBankFiltersComponent extends CustomFormControlAccessWithLoaders implements OnInit {

    categoriesList: PortailDocumentCategorieDto[] = [];

    statusList: PortailStatutDto[] = [];

    typesList: string[] = [];

    form: UntypedFormGroup;

    @Input() disabledFilters: PartialDocumentBankFilters = null;

    constructor(@Optional() @Inject(forwardRef(() => DocumentBankListComponent)) private bankListComponent: DocumentBankListComponent,
                private readonly graphqlService: GraphqlService,
                private readonly fb: UntypedFormBuilder,
                protected readonly cdr: ChangeDetectorRef) {
        super(cdr);
    }

    private _documentBankFilters = new BehaviorSubject<DocumentBankFilters>(null);

    get documentBankFilters(): DocumentBankFilters {
        return this._documentBankFilters.getValue();
    }

    set documentBankFilters(documentBankFilters: DocumentBankFilters) {
        this._documentBankFilters.next(documentBankFilters);
    }

    get documentBankFilters$(): Observable<DocumentBankFilters> {
        return this._documentBankFilters.asObservable();
    }

    writeValue(value: DocumentBankFilters): void {
        this.documentBankFilters = value;
        super.writeValue(value);
    }

    applyFilters(): void {
        super.notifyUpdate(this.form.value);
        this.closeFilters();
    }

    closeFilters(): void {
        this.bankListComponent.toggleFilters();
    }

    private initFilters(): void {
        this.loading = true;

        this.watch$ = this.graphqlService.mutateOne<'getInitialisationAdmninistrationDocuments', MutationGetInitialisationAdmninistrationDocumentsArgs>({
            mutation: gql`
                mutation initFilters($contexte: ContextInfosInput) {
                    getInitialisationAdmninistrationDocuments(contexte: $contexte) {
                        ListeCategories{
                            PortailDocumentCategorieDTO{
                                Id
                                Code
                                Libelle
                            }
                        }
                        ListeStatut{
                            PortailStatutDTO{
                                Id
                                Code
                                Libelle
                            }
                        }
                        ListeTypes{
                            string
                        }
                    }
                }
            `,
        }).pipe(
            tap((getInitialisationAdmninistrationDocuments) => {
                const {ListeCategories, ListeStatut, ListeTypes} = getInitialisationAdmninistrationDocuments;

                this.categoriesList = ListeCategories?.PortailDocumentCategorieDTO || [];
                this.statusList = ListeStatut?.PortailStatutDTO || [];
                this.typesList = ListeTypes?.string || [];

                this.watch$ = this.documentBankFilters$.pipe(
                    tap(e => this.form.patchValue(e, {emitEvent: false}))
                );
            }),
            wheneverEnd(() => this.loading = false)
        );
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            categoryCode: [null],
            type: [null],
            statusId: [null],
            hasExpired: [false],
            isUnlinked: [false]
        });

        this.initFilters();
    }

}
